import { z } from 'zod';
import { AccountSettingSchema } from './account-settings.dto';
import { CustomFieldSchema } from './custom-field.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { DescriptionMaxLength } from './types.dto';

export const FeedbackFormFilter = {
  All: 'all',
  Published: 'published',
  Unpublished: 'unpublished',
} as const;

export type FeedbackFormFilterType = (typeof FeedbackFormFilter)[keyof typeof FeedbackFormFilter];

export const FeedbackFormFilterSchema = z.nativeEnum(FeedbackFormFilter);

export const FeedbackFormFieldSchema = z.object({
  name: z.string(),
  order: z.number(),
  fieldId: z.number(),
  required: z.boolean(),
  priorityFrameworkId: z.number().nullish(),
});
export type FeedbackFormFieldDto = z.infer<typeof FeedbackFormFieldSchema>;

export const FeedbackFormSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  portalId: z.number().nullish(),
  portalName: z.string().nullish(),
  name: z.string().trim().min(1).max(300),
  description: z.string().max(DescriptionMaxLength).nullish(),
  formImageUrl: z.string().max(2083).nullish(),
  slug: z
    .string()
    .min(8)
    .max(200)
    .regex(/^[\w-]+$/),
  fields: z.array(FeedbackFormFieldSchema),
  summaryFieldName: z.string().max(300).nullish(),
  allowsAttachments: z.boolean(),
  enableAuthentication: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.date().nullable(),
  updatedAt: z.date().nullish(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  accountSettings: AccountSettingSchema.optional(),
});

export type FeedbackFormDto = z.infer<typeof FeedbackFormSchema>;

export const GetFeedbackFormWithFieldsSchema = FeedbackFormSchema.extend({
  customFields: z.array(CustomFieldSchema),
});
export type FeedbackFormWithFieldsDto = z.infer<typeof GetFeedbackFormWithFieldsSchema>;

export const GetFeedbackFormResponseSchema = inferApiResultSchema(FeedbackFormSchema);
export type GetFeedbackFormResponseDto = z.infer<typeof GetFeedbackFormResponseSchema>;

export const GetFeedbackFormWithFieldsResponseSchema = inferApiResultSchema(
  GetFeedbackFormWithFieldsSchema,
);
export type GetFeedbackFormWithFieldsResponseSchemaDto = z.infer<
  typeof GetFeedbackFormWithFieldsResponseSchema
>;

export const FeedbackFormProductsSchema = z
  .object({ id: z.number(), name: z.string(), archivedAt: z.coerce.date().nullish() })
  .array();
export type FeedbackFormProductsDto = z.infer<typeof FeedbackFormProductsSchema>;

export const GetFeedbackFormProductsResponseSchema = inferApiResultSchema(
  FeedbackFormProductsSchema,
);
export type GetFeedbackFormProductsResponseDto = z.infer<
  typeof GetFeedbackFormProductsResponseSchema
>;

export const CreateFeedbackFormSchema = FeedbackFormSchema.omit({
  id: true,
  accountId: true,
  portalName: true,
  createdAt: true,
  updatedAt: true,
  fields: true,
  isActive: true,
  allowsAttachments: true,
  deletedAt: true,
  archivedAt: true,
  enableAuthentication: true,
  accountSettings: true,
}).extend({
  portalId: z.number().nullish(),
  productAreaIds: z.array(z.number()).min(1), // Use min(1) instead of nonempty to avoid https://github.com/colinhacks/zod/discussions/3040
  fields: z.array(FeedbackFormFieldSchema).default([]),
  allowsAttachments: z.boolean().default(true),
  enableAuthentication: z.boolean().default(false),
  isActive: z.boolean().default(false),
});

export type CreateFeedbackFormDto = z.infer<typeof CreateFeedbackFormSchema>;

export const CreateFeedbackFormResponseSchema = inferApiResultSchema(FeedbackFormSchema);
export type CreateFeedbackFormReponseDto = z.infer<typeof CreateFeedbackFormResponseSchema>;

export const UpdateFeedbackFormSchema = FeedbackFormSchema.partial()
  .omit({
    accountId: true,
    portalName: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
    archivedAt: true,
  })
  .extend({ productAreaIds: z.array(z.number()).nullish() });
export type UpdateFeedbackFormDto = z.infer<typeof UpdateFeedbackFormSchema>;

export const UpdateFeedbackFormResponseSchema = inferApiResultSchema(FeedbackFormSchema);

export const DuplicateFeedbackFormSchema = z.object({
  name: z.string().trim().min(1).max(300),
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[\w-]+$/),
  portalId: z.number().nonnegative().optional(),
  productAreaIds: z.array(z.number()).min(1),
});
export type DuplicateFeedbackFormDto = z.infer<typeof DuplicateFeedbackFormSchema>;
export type UpdateFeedbackFormResponseDto = z.infer<typeof UpdateFeedbackFormResponseSchema>;

export const MoveFeedbackFormToPortalSchema = z.object({
  portalId: z.number().nonnegative().nullable(),
});
export type MoveFeedbackFormToPortalDto = z.infer<typeof MoveFeedbackFormToPortalSchema>;

export const RestoreFeedbackFormSchema = z.object({
  portalId: z.number().nonnegative().nullable(),
});
export type RestoreFeedbackFormDto = z.infer<typeof RestoreFeedbackFormSchema>;

export const GetFeedbackFormsResponseSchema = inferPaginatedApiResponse(
  z.array(FeedbackFormSchema),
);
export type GetFeedbackFormsResponseDto = z.infer<typeof GetFeedbackFormsResponseSchema>;
